import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { ImgComparisonSlider } from '@img-comparison-slider/react';
import { Settings } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ImageCarousel } from '@/components/ui/image-carousel';
import { env } from '@/constants/env';
import { useElementResize } from '@/hooks/use-element-resize';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';
import { CarouselImage } from '@/pages/image-rendering';

import { EDIT_IMAGE_METHOD, MAGIC_TOOL_OPTIONS } from '../constants/toggle-group-options';
import { ImageRenderingToolbar } from './toolbar';

interface ImageEditorProps {
  selectedImage: CarouselImage;
  imgRef: React.RefObject<HTMLImageElement>;
  imageId?: string;
  editMethod: EDIT_IMAGE_METHOD;
  magicToolOption: MAGIC_TOOL_OPTIONS;
  renderedImageIds: CarouselImage[];
  showMobileSettings: boolean;
  setShowMobileSettings: Dispatch<SetStateAction<boolean>>;
  onChangeMagicToolOption: (option: MAGIC_TOOL_OPTIONS) => void;
  onDownload: () => void;
  onChangeImage: (imageId: CarouselImage) => void;
}

const CAROUSEL_ITEM_WIDTH = 180;
const MOBILE_CAROUSEL_ITEM_WIDTH = 120;

export const ImageEditor = ({
  selectedImage,
  imgRef,
  renderedImageIds,
  imageId,
  editMethod,
  magicToolOption,
  showMobileSettings,
  setShowMobileSettings,
  onChangeMagicToolOption,
  onDownload,
  onChangeImage,
}: ImageEditorProps) => {
  const [favouriteIds, setFavouriteIds] = useState<string[]>([]);
  const { ref, size } = useElementResize();
  const isMobileView = !useResizeObserver(640);

  const showArrowButtons = useMemo(() => {
    if (ref?.current === null) return false;
    return (
      (size.width || (ref.current as HTMLDivElement)?.offsetWidth) <
      renderedImageIds?.length * (CAROUSEL_ITEM_WIDTH + 16)
    );
  }, [size, renderedImageIds, ref]);

  const handleFavourite = (id: string) => {
    setFavouriteIds((prev) =>
      prev.includes(id) ? prev.filter((imgId) => imgId !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (renderedImageIds?.length) {
      onChangeImage(renderedImageIds[renderedImageIds?.length - 1]);
    }
  }, [renderedImageIds, onChangeImage]);

  return (
    <div
      className={cn('relative w-full flex-col', !showMobileSettings ? 'flex' : 'hidden sm:flex')}
    >
      <div
        className={cn(
          'relative mx-auto flex min-h-[300px] flex-col justify-center overflow-hidden rounded-sm border border-solid border-text-primary bg-white',
          selectedImage?.mediaId ? '' : 'w-full'
        )}
        style={{ height: isMobileView ? 'calc(100vh - 180px)' : 'calc(100vh - 420px)' }}
      >
        {!!selectedImage && (
          <div className="absolute right-2 top-2 z-10">
            <ImageRenderingToolbar
              imageId={imageId}
              editMethod={editMethod}
              magicToolOption={magicToolOption}
              onChangeMagicToolOption={onChangeMagicToolOption}
              onDownload={onDownload}
            />
          </div>
        )}
        {imageId && !showMobileSettings ? (
          <Button
            className="absolute left-2 top-2 z-[100] flex h-12 w-12 items-center justify-center rounded-sm border-[1px] border-primary-light bg-text-primary px-1 hover:!bg-[#2C414B] sm:hidden"
            onClick={() => setShowMobileSettings(true)}
          >
            <Settings className="text-primary-light" />
          </Button>
        ) : null}
        <div className="flex h-full w-full items-center justify-center">
          {selectedImage ? (
            !selectedImage?.sourceId || selectedImage.mediaId === selectedImage.sourceId ? (
              <img
                ref={imgRef}
                crossOrigin="anonymous"
                className="h-full object-contain"
                src={`${env.API_URL}/images/${selectedImage.mediaId}`}
              />
            ) : (
              <ImgComparisonSlider className="max-h-full">
                <img
                  slot="first"
                  src={`${env.API_URL}/images/${selectedImage?.sourceId}`}
                  className="h-full w-full bg-white"
                />
                <img
                  ref={imgRef}
                  slot="second"
                  src={`${env.API_URL}/images/${selectedImage.mediaId}`}
                  className="bg-grey"
                />
              </ImgComparisonSlider>
            )
          ) : (
            <span className="text-center text-xl font-medium text-[#B2B2B2]">
              No image selected yet.
            </span>
          )}
        </div>
      </div>
      {!!selectedImage && (
        <div className="mt-2 h-16 sm:mt-5 sm:h-[110px]">
          <ImageCarousel
            ref={ref}
            className="absolute gap-2"
            imageWrapperClassName={(active: boolean) =>
              `rounded-sm border border-solid overflow-hidden max-w-52 ${active ? 'border-primary-light' : 'border-text-primary'}`
            }
            selectedImageId={selectedImage.mediaId}
            onClickImage={onChangeImage}
            favouriteIds={favouriteIds}
            onFavourite={handleFavourite}
            images={renderedImageIds}
            imageWidth={isMobileView ? MOBILE_CAROUSEL_ITEM_WIDTH : CAROUSEL_ITEM_WIDTH}
            showArrowButtons={showArrowButtons}
          />
        </div>
      )}
    </div>
  );
};
